import { z } from "zod";
import {
  MiDatePicker,
  MiDetailFields,
  MiModal,
  MiTimePicker,
} from "@miplatform/components";
import {
  DATEPICKER_ACTIONS,
  DATEPICKER_VARIANTS,
  INPUT_TYPES,
} from "@miplatform/constants";
import { useEdit } from "@miplatform/hooks";
import { ButtonVariants, Colors } from "@miplatform/types";
import { addWorkHours, getDate } from "@miplatform/utils";
import { mdiFileDocumentOutline } from "@mdi/js";
import { useTheme } from "@mui/material";
import { useLogFollowUpViewModel } from "./useLogFollowUpViewModel";

export const LogFollowUpModal = ({
  isOpen,
  attempt,
  calendar,
  onClose,
  onRefresh,
}) => {
  const theme = useTheme();
  const { followUpDefaultOffsetHours = 0 } = calendar || {};

  const edit = useEdit({
    contactName: "",
    reason: "",
    followUpDate: addWorkHours(followUpDefaultOffsetHours).minutes(0),
  });

  const handleClose = () => {
    edit.reset();
    onClose();
  };

  const { isLoading, hold } = useLogFollowUpViewModel({
    calendarId: calendar?.id,
    attempt,
    onClose: handleClose,
    onRefresh,
  });

  const followUpFields = [
    {
      label: "Contact Name",
      name: "contactName",
      required: true,
      width: "100%",
      validationRule: z.string().min(1, "Contact Name is required."),
    },
    {
      label: "Follow-Up Notes",
      name: "reason",
      multiline: true,
      rows: 3,
      width: "100%",
    },
    {
      label: "Next Follow-Up Date",
      name: "followUpDate",
      required: true,
      fieldType: INPUT_TYPES.CUSTOM,
      width: "170px",
      value: getDate(edit.getValue("followUpDate")),
      customComponent: (
        <MiDatePicker
          error={edit.errors?.followUpDate}
          onChange={(date) => {
            edit.update({
              followUpDate: date,
            });
          }}
          variant={DATEPICKER_VARIANTS.DESKTOP}
          actions={[DATEPICKER_ACTIONS.CANCEL]}
        />
      ),
    },
    {
      name: "followUpTime",
      fieldType: INPUT_TYPES.CUSTOM,
      width: "170px",
      required: true,
      value: getDate(edit.getValue("followUpDate")),
      customComponent: (
        <MiTimePicker
          shrink
          strictInterval
          timeSteps={{ minutes: 15 }}
          format="hh:mm A"
          label="Follow-Up Time"
          onChange={(time) => {
            edit.update({
              followUpDate: time,
            });
          }}
        />
      ),
    },
  ];

  edit.setValidation(followUpFields);

  return (
    <MiModal
      open={isOpen}
      closeModal={handleClose}
      width={370}
      titleIcon={mdiFileDocumentOutline}
      leftIconColor={theme.palette.primary.main}
      title="Log Follow-Up"
      loading={isLoading}
      actions={[
        {
          name: "Cancel",
          variant: ButtonVariants.Text,
          color: Colors.Black,
          onClick: handleClose,
        },
        {
          name: "Log Follow-Up",
          variant: ButtonVariants.Contained,
          disabled:
            !edit.isValid() ||
            !edit.isDirty() ||
            !getDate(edit.getValue("followUpDate"))?.isValid() ||
            isLoading,
          onClick: () => hold(edit.getValue()),
        },
      ]}
    >
      <MiDetailFields
        edit={edit}
        detailFields={followUpFields}
        customWrapperStyle={{ gap: 4 }}
      />
    </MiModal>
  );
};
